import { useContext, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./Card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./Table";
import { TopRepsSkeleton } from "./skeletons/TopRepsSkeleton";
import { add, set } from "date-fns";
import OptionsSelector from "./OptionsSelector";
import OrgApi from "../../../../API/OrgApi";
import anonUser from "../../../../assets/images/userGrey.png";
import { UserContext } from "../../../../App";

const rangeOptions = [
	{
		label: "Today",
		value: "TODAY",
		date: set(new Date(), {
			hours: 0,
			minutes: 0,
			seconds: 0,
			milliseconds: 0,
		}),
	},
	// {
	// 	label: "Last Week",
	// 	value: "LAST_WEEK",
	// 	date: add(
	// 		set(new Date(), {
	// 			hours: 0,
	// 			minutes: 0,
	// 			seconds: 0,
	// 			milliseconds: 0,
	// 		}),
	// 		{ days: -7 },
	// 	),
	// },
	{
		label: "This Month",
		value: "THIS_MONTH",
		date: set(new Date(), {
			hours: 0,
			minutes: 0,
			seconds: 0,
			milliseconds: 0,
			date: 0,
		}),
	},
	{
		label: "Last Month",
		value: "LAST_MONTH",
		date: add(
			set(new Date(), {
				hours: 0,
				minutes: 0,
				seconds: 0,
				milliseconds: 0,
				date: 0,
			}),
			{ months: -1 },
		),
	},
	{
		label: "This Year",
		value: "THIS_YEAR",
		date: set(new Date(), {
			hours: 0,
			minutes: 0,
			seconds: 0,
			milliseconds: 0,
			date: 0,
			month: 0,
		}),
	},
	{
		label: "Last Year",
		value: "LAST_YEAR",
		date: add(
			set(new Date(), {
				hours: 0,
				minutes: 0,
				seconds: 0,
				milliseconds: 0,
				date: 0,
				month: 0,
			}),
			{ years: -1 },
		),
	},
];

export function TopReps({ fiberCompanyId }) {
	const [data, setData] = useState({
		sales: [],
		installed: [],
	});
	const [allReps, setAllReps] = useState([]);
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState("SALES");
	const [selectedRange, setSelectedRange] = useState(rangeOptions[0]);
	var user = useContext(UserContext);

	useEffect(() => {
		fetchData();
	}, [selectedRange, fiberCompanyId, user]);

	const fetchData = async () => {
		setLoading(true);
		const res = await OrgApi.getDashboardTopRepsData({
			minDate: selectedRange.date,
			fiberCompanyId,
			userId: user.id,
		});

		const { allReps, ...data } = res.data;

		setAllReps(allReps);
		setData(data);

		setLoading(false);
	};

	const activeData = useMemo(
		() => (activeTab === "SALES" ? data.sales : data.installed),
		[activeTab, data],
	);

	if (loading) return <TopRepsSkeleton />;

	return (
		<Card className="flex-1 lg:flex-initial">
			<CardHeader className="flex flex-col pb-4 gap-4 items-start lg:flex-row lg:items-center lg:justify-between">
				<div className="flex gap-4 items-center justify-between w-full lg:w-auto">
					<CardTitle className="text-lg font-semibold ">Top Reps</CardTitle>
					<button className="px-3.5 py-2.5 font-semibold text-sm border shadow-sm rounded-lg lg:hidden">
						View Leaderboard
					</button>
				</div>

				<div className="flex flex-row gap-5 w-full justify-between lg:flex-row-reverse lg:flex-1 lg:w-auto">
					<OptionsSelector
						options={[
							{ label: "Sales", value: "SALES" },
							{ label: "Installs", value: "INSTALLS" },
						]}
						onSelect={setActiveTab}
						selected={activeTab}
					/>

					<select
						className="flex-1 max-w-44 border shadow-sm rounded-lg py-2.5 px-3.5 text-sm"
						value={selectedRange.value}
						onChange={(e) =>
							setSelectedRange(
								rangeOptions.find((r) => r.value === e.target.value),
							)
						}
					>
						{rangeOptions.map((r) => (
							<option value={r.value}>{r.label}</option>
						))}
					</select>
				</div>
			</CardHeader>
			<CardContent>
				{!activeData.length ? (
					<div className="p-10 flex justify-center items-center">
						<div className="text-sm font-semibold text-gray-400">No Data</div>
					</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-[60px] text-gray-400">Rank</TableHead>
								<TableHead className="text-gray-400">Rep Name</TableHead>
								<TableHead className="w-[60px] text-gray-400">
									{activeTab === "SALES" ? "Sales" : "Installs"}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{activeData.map(({ id, number }, i) => (
								<TableRow key={id}>
									<TableCell className="font-medium text-center">
										{i + 1}
									</TableCell>
									<TableCell className="flex items-center gap-2">
										<img
											src={
												allReps.find((rep) => rep.id === id)?.imageUrl ??
												anonUser
											}
											style={{
												width: 35,
												height: 35,
												borderRadius: "50%",
											}}
											alt="sales rep"
										/>
										{allReps.find((rep) => rep.id === id)?.name ?? `Rep ${id}`}
									</TableCell>
									<TableCell className="text-center">{number}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	);
}
